import React, { useEffect } from "react";
import { Box, Divider, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../shared/FieldGroup";
import ScalePointRow from "../elements/ScalePointRow";
import {
  normalizeRatingScaleConfig,
  RATING_SCALE_LEVELS,
  type TargetFormValues,
  type RatingScaleConfig,
} from "../TargetBuilderPanel.types";

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "once-per-session", label: "Once per session" },
  { value: "per-activity", label: "Per activity" },
];

/**
 * Rating scale anchors config — rendered as a standalone card below the L2
 * selector when `l2_method === "anchored-rating-scale"`. Owns the
 * `values.l1_sub_config` shape (RatingScaleConfig) via Formik.
 */
const L2RatingScaleConfig: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();

  const storedConfig = values.l1_sub_config as RatingScaleConfig | null;
  const isStoredValid =
    storedConfig != null &&
    Array.isArray(storedConfig.anchors) &&
    storedConfig.anchors.length === RATING_SCALE_LEVELS.length &&
    RATING_SCALE_LEVELS.every((level, index) => storedConfig.anchors[index]?.level === level);

  useEffect(() => {
    if (!isStoredValid) {
      setFieldValue("l1_sub_config", normalizeRatingScaleConfig(storedConfig));
    }
  }, [isStoredValid, setFieldValue, storedConfig]);

  const config: RatingScaleConfig = isStoredValid
    ? storedConfig
    : normalizeRatingScaleConfig(storedConfig);

  const update = (partial: Partial<RatingScaleConfig>) =>
    setFieldValue("l1_sub_config", { ...config, ...partial });

  const updateAnchor = (level: number, partial: Partial<RatingScaleConfig["anchors"][number]>) => {
    update({
      anchors: config.anchors.map((a) => (a.level === level ? { ...a, ...partial } : a)),
    });
  };

  return (
    <Stack gap={4}>
      <Box>
        <Typography variant="subtitle2">Rating Scale Anchors</Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Define operational definitions for each scale point (0-5)
        </Typography>
      </Box>

      <FieldGroup label="Rating Frequency" description="How often to record a rating" required>
        <Select
          size="small"
          fullWidth
          value={config.rating_frequency}
          onChange={(e) =>
            update({ rating_frequency: e.target.value as RatingScaleConfig["rating_frequency"] })
          }
          aria-label="Rating frequency"
          displayEmpty
          renderValue={(v) =>
            v ? (
              FREQUENCY_OPTIONS.find((o) => o.value === v)?.label
            ) : (
              <Typography variant="body2" color="text.disabled">
                Select frequency...
              </Typography>
            )
          }
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FieldGroup>

      <FieldGroup label="Scale Point Definitions">
        <Stack gap={2}>
          {config.anchors.map((anchor) => (
            <ScalePointRow
              key={anchor.level}
              level={anchor.level}
              title={anchor.title}
              description={anchor.description}
              placeholder={anchor.placeholder}
              onTitleChange={(title) => updateAnchor(anchor.level, { title })}
              onDescriptionChange={(description) => updateAnchor(anchor.level, { description })}
            />
          ))}
        </Stack>
      </FieldGroup>

      <Divider />

      <Box>
        <Typography component="p" variant="caption" color="text.secondary" fontWeight="bold">
          Scale Preview
        </Typography>
        <Stack direction="row" justifyContent="space-around" mt={2} gap={2}>
          {config.anchors.map((anchor) => (
            <Stack key={anchor.level} alignItems="center" gap={1} flex={1} minWidth={0}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                gap={1}
                aria-hidden
                sx={(theme) => ({
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: (theme.palette as any).grayscale?.[100] ?? "#E2E8F0",
                  color: "text.secondary",
                  fontSize: 13,
                  fontWeight: 500,
                })}
              >
                {anchor.level}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={anchor.title || String(anchor.level)}
              >
                {anchor.title || anchor.level}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default L2RatingScaleConfig;
